/* ============================================================================
   VILLAINS TCG — FAQ page (cst-faq-page.js)
   Progressive enhancement over a server-rendered <details> accordion (works fully with
   JS off). Adds: live search with match highlighting, category-chip filtering, an empty
   state, single-open mode (editor option), and deep-link support (#faq-id opens + scrolls
   to a question, and opening a question updates the URL hash so it's shareable).
   Re-inits on shopify:section:load for the Theme Editor.
   ========================================================================== */
(() => {
  'use strict';
  const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');
  const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  const init = (root) => {
    if (!root || root.dataset.cstBound) return;
    root.dataset.cstBound = '1';

    const single = root.hasAttribute('data-single');
    const input = root.querySelector('[data-cst-faq-search]');
    const clearBtn = root.querySelector('[data-cst-faq-clear]');
    const chips = [...root.querySelectorAll('[data-cst-faq-cat]')];
    const items = [...root.querySelectorAll('[data-cst-faq-item]')];
    const empty = root.querySelector('[data-cst-faq-empty]');
    const termEl = root.querySelector('[data-cst-faq-term]');
    const resetBtn = root.querySelector('[data-cst-faq-reset]');
    if (!items.length) return;

    let term = '';
    let cat = 'all';

    const highlight = (item) => {
      const q = item.querySelector('[data-cst-faq-q]');
      if (!q) return;
      const orig = q.dataset.orig != null ? q.dataset.orig : (q.dataset.orig = q.textContent);
      if (!term) { q.textContent = orig; return; }
      const low = orig.toLowerCase();
      if (!low.includes(term)) { q.textContent = orig; return; }
      let out = '', rest = orig, r = low, i;
      while ((i = r.indexOf(term)) >= 0) {
        out += esc(rest.slice(0, i)) + '<mark class="cst-faq-mark">' + esc(rest.slice(i, i + term.length)) + '</mark>';
        rest = rest.slice(i + term.length); r = r.slice(i + term.length);
      }
      q.innerHTML = out + esc(rest);
    };

    const restore = (item) => { const q = item.querySelector('[data-cst-faq-q]'); if (q && q.dataset.orig != null) q.textContent = q.dataset.orig; };

    const apply = () => {
      let shown = 0;
      items.forEach((item) => {
        const okCat = cat === 'all' || item.dataset.cat === cat;
        const okTerm = !term || (item.dataset.search || '').indexOf(term) >= 0;
        const vis = okCat && okTerm;
        item.classList.toggle('is-hidden', !vis);
        if (vis) { shown++; highlight(item); } else { restore(item); }
      });
      if (empty) empty.hidden = shown > 0;
      if (empty && termEl && shown === 0) termEl.textContent = term ? '“' + input.value.trim() + '”' : 'those filters';
    };

    const setCat = (next) => {
      cat = next || 'all';
      chips.forEach((c) => { const on = (c.dataset.cstFaqCat || 'all') === cat; c.classList.toggle('is-on', on); c.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    };

    if (input) {
      input.addEventListener('input', debounce(() => {
        term = input.value.trim().toLowerCase();
        if (clearBtn) clearBtn.hidden = !input.value;
        apply();
      }, 110));
    }
    if (clearBtn && input) clearBtn.addEventListener('click', () => { input.value = ''; term = ''; clearBtn.hidden = true; apply(); input.focus(); });

    chips.forEach((chip) => chip.addEventListener('click', () => { setCat(chip.dataset.cstFaqCat); apply(); }));

    if (resetBtn) resetBtn.addEventListener('click', () => {
      if (input) input.value = '';
      term = ''; if (clearBtn) clearBtn.hidden = true; setCat('all'); apply();
      if (input) input.focus();
    });

    /* single-open behaviour + keep the URL hash in sync with the open question */
    items.forEach((item) => {
      item.addEventListener('toggle', () => {
        if (!item.open) return;
        if (single) items.forEach((o) => { if (o !== item) o.open = false; });
        try { history.replaceState(null, '', '#' + item.id); } catch (_) { /* noop */ }
      });
    });

    /* deep-link: open the linked question and scroll to it (clears filters so it shows) */
    const openFromHash = () => {
      const id = decodeURIComponent((window.location.hash || '').slice(1));
      if (!id) return;
      const item = document.getElementById(id);
      if (!item || !item.hasAttribute('data-cst-faq-item')) return;
      if (input) input.value = '';
      term = ''; if (clearBtn) clearBtn.hidden = true; setCat('all'); apply();
      item.open = true;
      window.requestAnimationFrame(() => item.scrollIntoView({ behavior: REDUCE.matches ? 'auto' : 'smooth', block: 'start' }));
    };
    openFromHash();
    window.addEventListener('hashchange', openFromHash);
  };

  const scan = (root) => {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-cst-faq]').forEach(init);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document));
  else scan(document);
  document.addEventListener('shopify:section:load', (e) => scan(e.target || document));
})();
